/**
 * Accessibility Statement Page
 * Simple route page for the accessibility statement.
 * Uses same layout pattern as other policy pages (Terms, Privacy, Cookie).
 * Uses shared Navbar from components/landingPage/Navbar (via layout).
 */

import React, { useEffect } from 'react'
import { motion } from 'motion/react'
import SEO from '@/components/seo/SEO'
import { PUBLIC_PAGE_SEO } from '@/constants/publicPageSeo'
import './AccessibilityStatementPage.css'

const AccessibilityStatementPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <div className='digipalz-accessibility__main-container'>
      <SEO {...PUBLIC_PAGE_SEO.accessibility} />
      {/* Premium Background Effects - blurred orbs + grid overlay */}
      <div className='digipalz-accessibility__bg-effects'>
        <div className='digipalz-accessibility__bg-orb digipalz-accessibility__bg-orb--1' />
        <div className='digipalz-accessibility__bg-orb digipalz-accessibility__bg-orb--2' />
        <div className='digipalz-accessibility__bg-orb digipalz-accessibility__bg-orb--3' />
        <div className='digipalz-accessibility__bg-grid' />
      </div>
      <div className='digipalz-accessibility__content'>
        {/* Navbar comes from layout (shared components/landingPage/Navbar) */}

        {/* Main Content - motion wrapper (max-w 1200px, py-16 md:py-24) + white card inside */}
        <motion.div
          className='digipalz-accessibility__main-content'
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className='digipalz-accessibility__content-wrapper'>
            <motion.div
              className='digipalz-accessibility__title-motion'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h1 className='digipalz-accessibility__title'>
                <span className='digipalz-accessibility__title-line1'>
                  Accessibility{' '}
                </span>
                <span className='digipalz-accessibility__title-line2'>
                  Statement
                </span>
              </h1>
              <div className='digipalz-accessibility__title-bar' />
            </motion.div>
            <motion.div
              className='digipalz-accessibility__intro digipalz-accessibility__intro-motion'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <p>
                Digipalz is committed to making its website reasonably
                accessible to users of all abilities. We aim to design and
                maintain our{' '}
                <span className='digipalz-accessibility__intro-line2'>
                  website in a manner that is inclusive and usable for a broad
                  audience.
                </span>
              </p>
              <p>
                Our goal is to align, where reasonably practicable, with
                generally recognized accessibility standards, including the Web
                Content Accessibility Guidelines (WCAG) 2.1 Level AA.
                Accessibility considerations are incorporated into our design
                and development processes on an ongoing basis.
              </p>
              <p>
                While we strive to improve accessibility, we cannot guarantee
                that all content or functionality will be accessible at all
                times. Certain third-party content or features that are not
                under our control may not fully conform to accessibility
                standards.
              </p>
              <p>
                Accessibility is an ongoing effort, and we continue to review
                and update our website as technologies and standards evolve.
              </p>
            </motion.div>
            <div className='digipalz-accessibility__intro-border' />
            <h2 className='digipalz-accessibility__subheading'>
              Accessibility Standards
            </h2>
            <p className='digipalz-accessibility__subtext'>
              Every child deserves access to digital safety education. Our
              design must be inclusive by default.
            </p>

            {/* WCAG 2.1 AA Compliance card */}
            <div className='digipalz-accessibility__wcag-card'>
              <h3 className='digipalz-accessibility__wcag-title'>
                WCAG 2.1 AA Compliance
              </h3>
              <div className='digipalz-accessibility__wcag-grid'>
                {/* Color Contrast Ratios */}
                <div className='digipalz-accessibility__wcag-column'>
                  <h4 className='digipalz-accessibility__wcag-heading digipalz-accessibility__wcag-heading--purple'>
                    Color Contrast Ratios
                  </h4>
                  <div className='digipalz-accessibility__wcag-list'>
                    <div className='digipalz-accessibility__wcag-item'>
                      <span
                        className='digipalz-accessibility__wcag-icon'
                        aria-hidden
                      >
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                          <path d='m9 11 3 3L22 4' />
                        </svg>
                      </span>
                      <div>
                        <p className='digipalz-accessibility__wcag-label'>
                          Normal Text (16px+)
                        </p>
                        <p className='digipalz-accessibility__wcag-detail'>
                          4.5:1 minimum
                        </p>
                        <p className='digipalz-accessibility__wcag-note'>
                          Use Gray-600 (#525252) or darker on white backgrounds
                        </p>
                      </div>
                    </div>
                    <div className='digipalz-accessibility__wcag-item'>
                      <span
                        className='digipalz-accessibility__wcag-icon'
                        aria-hidden
                      >
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                          <path d='m9 11 3 3L22 4' />
                        </svg>
                      </span>
                      <div>
                        <p className='digipalz-accessibility__wcag-label'>
                          Large Text (24px+)
                        </p>
                        <p className='digipalz-accessibility__wcag-detail'>
                          3:1 minimum
                        </p>
                        <p className='digipalz-accessibility__wcag-note'>
                          Can use Gray-500 (#737373) for large headings
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Interactive Elements */}
                <div className='digipalz-accessibility__wcag-column'>
                  <h4 className='digipalz-accessibility__wcag-heading digipalz-accessibility__wcag-heading--blue'>
                    Interactive Elements
                  </h4>
                  <div className='digipalz-accessibility__wcag-list'>
                    <div className='digipalz-accessibility__wcag-item'>
                      <span
                        className='digipalz-accessibility__wcag-icon'
                        aria-hidden
                      >
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                          <path d='m9 11 3 3L22 4' />
                        </svg>
                      </span>
                      <div>
                        <p className='digipalz-accessibility__wcag-label'>
                          Minimum Touch Target: 44x44px
                        </p>
                        <p className='digipalz-accessibility__wcag-note'>
                          All buttons, links, and interactive elements
                        </p>
                      </div>
                    </div>
                    <div className='digipalz-accessibility__wcag-item'>
                      <span
                        className='digipalz-accessibility__wcag-icon'
                        aria-hidden
                      >
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                          <path d='m9 11 3 3L22 4' />
                        </svg>
                      </span>
                      <div>
                        <p className='digipalz-accessibility__wcag-label'>
                          Keyboard Navigation
                        </p>
                        <p className='digipalz-accessibility__wcag-note'>
                          All functionality accessible via keyboard
                        </p>
                      </div>
                    </div>
                    <div className='digipalz-accessibility__wcag-item'>
                      <span
                        className='digipalz-accessibility__wcag-icon'
                        aria-hidden
                      >
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                          <path d='m9 11 3 3L22 4' />
                        </svg>
                      </span>
                      <div>
                        <p className='digipalz-accessibility__wcag-label'>
                          Focus Indicators
                        </p>
                        <p className='digipalz-accessibility__wcag-note'>
                          Visible focus rings on all interactive elements
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cognitive Accessibility for Children */}
            <div className='digipalz-accessibility__cognitive-card'>
              <h3 className='digipalz-accessibility__cognitive-title'>
                Cognitive Accessibility for Children
              </h3>
              <p className='digipalz-accessibility__cognitive-desc'>
                Ages 8-12 includes a wide range of reading levels and cognitive
                abilities. Our design must accommodate all learners.
              </p>
              <div className='digipalz-accessibility__cognitive-grid'>
                <div className='digipalz-accessibility__cognitive-item digipalz-accessibility__cognitive-item--purple'>
                  <h4 className='digipalz-accessibility__cognitive-heading digipalz-accessibility__cognitive-heading--green'>
                    ✓ Reading Level
                  </h4>
                  <ul className='digipalz-accessibility__cognitive-list'>
                    <li>• Target 3rd-5th grade reading level</li>
                    <li>• Short sentences (max 15 words)</li>
                    <li>• Common vocabulary only</li>
                    <li>• Define technical terms inline</li>
                  </ul>
                </div>
                <div className='digipalz-accessibility__cognitive-item digipalz-accessibility__cognitive-item--blue'>
                  <h4 className='digipalz-accessibility__cognitive-heading digipalz-accessibility__cognitive-heading--blue'>
                    ✓ Visual Clarity
                  </h4>
                  <ul className='digipalz-accessibility__cognitive-list'>
                    <li>• High contrast (never gray on gray)</li>
                    <li>• Generous white space (8pt grid)</li>
                    <li>• Clear visual hierarchy</li>
                    <li>• Icons + text labels (never icons alone)</li>
                  </ul>
                </div>
                <div className='digipalz-accessibility__cognitive-item digipalz-accessibility__cognitive-item--purple'>
                  <h4 className='digipalz-accessibility__cognitive-heading digipalz-accessibility__cognitive-heading--purple'>
                    ✓ Information Design
                  </h4>
                  <ul className='digipalz-accessibility__cognitive-list'>
                    <li>• One concept per screen</li>
                    <li>• Progressive disclosure</li>
                    <li>• Clear next steps</li>
                    <li>• Error messages that help, not shame</li>
                  </ul>
                </div>
                <div className='digipalz-accessibility__cognitive-item digipalz-accessibility__cognitive-item--pink'>
                  <h4 className='digipalz-accessibility__cognitive-heading digipalz-accessibility__cognitive-heading--pink'>
                    ✓ Neurodivergent Support
                  </h4>
                  <ul className='digipalz-accessibility__cognitive-list'>
                    <li>
                      • Customizable audio controls with full mute options
                    </li>
                    <li>
                      • Optional sound settings in every interactive activity
                    </li>
                    <li>
                      • Automatic progress saving — learn at your own pace
                    </li>
                    <li>
                      • Flexible learning sessions (pause and resume anytime)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Us section */}
            <div className='digipalz-accessibility__contact'>
              <h2 className='digipalz-accessibility__contact-title'>
                Contact Us
              </h2>
              <p className='digipalz-accessibility__contact-desc digipalz-accessibility__contact-desc--first'>
                If you experience difficulty accessing any part of our website,
                please contact us at:
              </p>
              <motion.a
                href='mailto:customersupport@digipalz.io'
                className='digipalz-accessibility__contact-btn'
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span
                  className='digipalz-accessibility__contact-btn-icon'
                  aria-hidden
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <rect width='20' height='16' x='2' y='4' rx='2' />
                    <path d='m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' />
                  </svg>
                </span>
                customersupport@digipalz.io
              </motion.a>
              <p className='digipalz-accessibility__contact-desc digipalz-accessibility__contact-desc--second'>
                We will make reasonable efforts to address accessibility
                concerns in a timely manner.
              </p>
            </div>
            <div className='digipalz-accessibility__section-border' />
            <p className='digipalz-accessibility__disclaimer'>
              This statement may be updated from time to time without notice.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default AccessibilityStatementPage
